import React, { useState, useEffect } from 'react'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import { t } from '../../components/i18n/i18n'
import "../../assets/css/common.css"
import "./newsModal.css"
import back from "../../assets/images/elements/aboutHeader.jpg"

const PAGE_SIZE = 6

const formatDate = (ts) => {
  if (!ts) return ''
  return new Date(ts.seconds * 1000).toLocaleDateString('uk-UA', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

const NewsModal = ({ item, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return (
    <div className='news-modal-overlay' onClick={onClose}>
      <div className='news-modal' onClick={e => e.stopPropagation()}>
        <button style={s.closeBtn} onClick={onClose} aria-label='Закрити'>✕</button>

        {item.photoUrl && (
          <div style={s.modalImgWrap}>
            <img src={item.photoUrl} alt={item.title} style={s.modalImg} />
          </div>
        )}

        <div style={s.modalBody}>
          <div style={s.date}>{formatDate(item.createdAt)}</div>
          <h2 style={s.modalTitle}>{item.title}</h2>
          <p style={s.modalText}>{item.content}</p>
        </div>
      </div>
    </div>
  )
}

const getPageNumbers = (current, total) => {
  const pages = []
  const add = (p) => { if (!pages.includes(p)) pages.push(p) }

  add(1)
  for (let p = current - 1; p <= current + 1; p++) {
    if (p > 1 && p < total) add(p)
  }
  if (total > 1) add(total)

  const withGaps = []
  let prev = 0
  for (const p of pages.sort((a, b) => a - b)) {
    if (prev && p - prev > 1) withGaps.push('…')
    withGaps.push(p)
    prev = p
  }
  return withGaps
}

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null

  return (
    <div style={s.pagination}>
      <button
        style={{ ...s.pageBtn, ...(currentPage === 1 ? s.pageBtnDisabled : {}) }}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ←
      </button>

      {getPageNumbers(currentPage, totalPages).map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} style={s.pageGap}>…</span>
        ) : (
          <button
            key={p}
            style={{ ...s.pageBtn, ...(p === currentPage ? s.pageBtnActive : {}) }}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        )
      )}

      <button
        style={{ ...s.pageBtn, ...(currentPage === totalPages ? s.pageBtnDisabled : {}) }}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    </div>
  )
}

export const NewsFeed = () => {
  const [news, setNews] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeItem, setActiveItem] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(q, snap => {
      setNews(snap.docs.map(d => ({ id: d.id, ...d.data() })))
      setLoading(false)
    }, () => setLoading(false))
    return unsub
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const filteredNews = news.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.trim().toLowerCase())
  )

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const pageNews = filteredNews.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const goToPage = (page) => {
    const clamped = Math.max(1, Math.min(page, totalPages))
    setCurrentPage(clamped)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <section className='text_container_light'>
      <div className='container_100'>

        {/* Header banner */}
        <div className='backImg15'>
          <img src={back} alt='' />
          <div className='text_container_dark_15_top'>
            <div className='h1_15 margin0'>{t('newsfeedTitle')}</div>
            <center>
              <input
                type='text'
                placeholder={t('search')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </center>
          </div>
        </div>

        <div className='container boxItems' style={{ marginTop: '30px' }}>

          {loading && (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>•••</p>
          )}

          {!loading && news.length === 0 && (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>
              {t('newsfeedEmpty')}
            </p>
          )}

          {!loading && news.length > 0 && filteredNews.length === 0 && (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>
              {t('newsfeedNoMatch')}
            </p>
          )}

          <div style={s.grid}>
            {pageNews.map(item => (
              <article key={item.id} style={s.card}>
                {item.photoUrl && (
                  <div style={s.imgWrap}>
                    <img src={item.photoUrl} alt={item.title} style={s.img} />
                  </div>
                )}
                <div style={s.body}>
                  <div style={s.date}>{formatDate(item.createdAt)}</div>
                  <h2 style={s.title}>{item.title}</h2>

                  {item.content && (
                    <div style={s.content}>
                      <p style={s.text}>
                        {item.content.slice(0, 200)}{item.content.length > 200 ? '…' : ''}
                      </p>
                      {item.content.length > 200 && (
                        <button style={s.readMore} onClick={() => setActiveItem(item)}>
                          {t('newsfeedReadMore')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>

          <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={goToPage} />

        </div>
      </div>

      {activeItem && <NewsModal item={activeItem} onClose={() => setActiveItem(null)} />}
    </section>
  )
}

const s = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '28px',
    marginTop: '8px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    border: '1px solid #f0e4d4',
    display: 'flex',
    flexDirection: 'column',
  },
  imgWrap: {
    width: '100%',
    height: '200px',
    overflow: 'hidden',
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  body: {
    padding: '20px 22px 22px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  date: {
    fontSize: '12px',
    color: '#b8a090',
    marginBottom: '8px',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d1f14',
    margin: '0 0 12px',
    lineHeight: '1.35',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  content: {
    flex: 1,
  },
  text: {
    fontSize: '14px',
    color: '#5a5a5a',
    lineHeight: '1.7',
    margin: '0 0 12px',
    whiteSpace: 'pre-wrap',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  readMore: {
    background: 'none',
    border: 'none',
    color: '#8b6044',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },

  /* Pagination */
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '36px',
    flexWrap: 'wrap',
  },
  pageBtn: {
    minWidth: '38px',
    height: '38px',
    padding: '0 10px',
    border: '1.5px solid #e8d8c8',
    background: '#fff',
    color: '#6b4a32',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  pageBtnActive: {
    background: 'linear-gradient(135deg, #6b4a32, #8b6044)',
    color: '#fff',
    borderColor: 'transparent',
  },
  pageBtnDisabled: {
    opacity: 0.4,
    cursor: 'default',
  },
  pageGap: {
    color: '#b8a090',
    fontSize: '14px',
    padding: '0 4px',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },

  /* Modal */
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(0,0,0,0.5)',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalImgWrap: {
    width: '100%',
    maxHeight: '420px',
    overflow: 'hidden',
    borderRadius: '16px 16px 0 0',
  },
  modalImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  modalBody: {
    padding: '28px 32px 36px',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2d1f14',
    margin: '0 0 16px',
    lineHeight: '1.3',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  modalText: {
    fontSize: '15px',
    color: '#444',
    lineHeight: '1.8',
    whiteSpace: 'pre-wrap',
    margin: 0,
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
}
